import React from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const Accessibility = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="legal-page-container">
      <SEO
        title="Accessibility Statement"
        description="Star.Ryhwa website accessibility statement and commitment to inclusive shopping."
        canonicalUrl="/accessibility"
      />
      <div className="legal-page-inner">
        {/* Breadcrumb Bar */}
        <div className="legal-breadcrumbs">
          <Link to="/">Home</Link>
          <span className="legal-breadcrumb-separator">/</span>
          <span>Trust &amp; Legal Center</span>
          <span className="legal-breadcrumb-separator">/</span>
          <span className="legal-breadcrumb-current">Accessibility Statement</span>
        </div>

        {/* Document Header */}
        <header className="legal-doc-header">
          <h1 className="legal-doc-title">Accessibility Statement</h1>
          <p className="legal-doc-description">
            Our active commitment to providing barrier-free, inclusive digital shopping experiences aligned with WCAG 2.1 Level AA and Title III of the Americans with Disabilities Act.
          </p>

          <div className="legal-meta-bar">
            <div className="legal-meta-items">
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Last Updated: <strong>September 2026</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Estimated Read: <strong>3 min read</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4"/>
                  <path d="M12 16h.01"/>
                </svg>
                Standard: <strong>WCAG 2.1 Level AA</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Framework: <strong>ADA Title III</strong>
              </span>
            </div>
            <button type="button" onClick={handlePrint} className="legal-print-btn" title="Print or Save as PDF">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print / Save PDF
            </button>
          </div>
        </header>

        {/* Navigation Tab Bar */}
        <LegalNav />

        {/* Executive Summary */}
        <div className="legal-doc-summary">
          <h2>Summary of Key Principles</h2>
          <ul>
            <li><strong>WCAG 2.1 Level AA Compliance:</strong> We build and audit our interfaces against recognized global digital accessibility standards.</li>
            <li><strong>Assistive Technology Support:</strong> Structured with semantic HTML, high contrast ratios, visible keyboard focus indicators, and screen reader compatibility.</li>
            <li><strong>Personalized Ordering Accommodations:</strong> If you face any barrier while purchasing or browsing, we provide direct one-on-one ordering assistance.</li>
            <li><strong>Fast 2-Day Response SLA:</strong> Our studio responds to all accessibility requests and feedback within two (2) business days.</li>
          </ul>
        </div>

        {/* Main Two-Column Layout */}
        <div className="legal-grid-layout">
          {/* Main Content Column */}
          <article className="legal-main-content">
            {/* Section 1 */}
            <section id="section-1" className="legal-doc-section">
              <h2>1. Our Commitment to Digital Accessibility</h2>
              <p>
                Star.Ryhwa is firmly committed to ensuring digital accessibility for all users, including individuals with disabilities. We believe everyone deserves equal, barrier-free access to our handmade art and collectibles.
              </p>
              <p>
                We continually strive to adhere to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong> standards, which serve as the recognized technical benchmark under Title III of the Americans with Disabilities Act (ADA).
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="legal-doc-section">
              <h2>2. Accessibility Measures &amp; Technical Standards</h2>
              <p>To support accessibility across our online store, we design with the following principles:</p>
              <ul>
                <li><strong>Perceivable:</strong> We provide text alternatives for non-text visual content, maintain high-contrast color ratios for readability, and use semantic HTML markup compatible with screen readers (e.g., NVDA, VoiceOver, JAWS).</li>
                <li><strong>Operable:</strong> All interactive elements—including product filters, navigation tabs, modal dialogs, and checkout controls—are fully navigable via keyboard controls with clear visible focus rings.</li>
                <li><strong>Understandable:</strong> Forms provide clear labels, error notifications, and straightforward instruction. Product specifications and grading terminology are clearly explained.</li>
                <li><strong>Robust:</strong> Code is tested across modern desktop and mobile browsers to ensure seamless compatibility with assistive technologies.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="legal-doc-section">
              <h2>3. Ongoing Improvements &amp; Known Limitations</h2>
              <p>
                As an independent, small-batch artisanal studio, we are constantly expanding our digital accessibility. Some archival product photos or user-generated collage images may currently have abbreviated descriptive alt text. We are actively auditing our catalog to enhance alt descriptions and streamline checkout navigation for screen reader users.
              </p>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="legal-doc-section">
              <h2>4. Personalized Accommodations &amp; Ordering Assistance</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>Dedicated Accommodation Guarantee:</strong> If you encounter any difficulty accessing information, navigating our shop, or completing a transaction due to a disability, we are honored to personally assist you.
                </p>
              </div>
              <p>
                Please reach out to our studio team through either channel below:
              </p>
              <ul className="legal-contact-list">
                <li><strong>Email Accommodation Desk:</strong> <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a></li>
                <li><strong>Instagram Direct Message:</strong> <a href="https://www.instagram.com/star.ryhwa" target="_blank" rel="noopener noreferrer">@star.ryhwa</a></li>
              </ul>
              <p>
                We aim to respond to all accessibility and accommodation requests <strong>within 2 business days</strong>. We can manually assist with custom orders, alternative invoices, or detailed product descriptions.
              </p>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="legal-doc-section">
              <h2>5. Accessibility Feedback &amp; Contact Information</h2>
              <p>
                We welcome your feedback on how to improve accessibility. If you encounter any barriers or have recommendations, please contact our studio:
              </p>
              <ul className="legal-contact-list">
                <li><strong>Email:</strong> <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a></li>
                <li><strong>Instagram:</strong> <a href="https://www.instagram.com/star.ryhwa" target="_blank" rel="noopener noreferrer">@star.ryhwa</a></li>
                <li><strong>Studio Location:</strong> State of Hawaii, United States</li>
              </ul>
            </section>
          </article>

          {/* Sticky Table of Contents Sidebar */}
          <aside className="legal-sidebar">
            <div className="legal-toc-container">
              <div className="legal-toc-title">On This Page</div>
              <ul className="legal-toc-list">
                <li><a href="#section-1" className="legal-toc-link">1. Accessibility Commitment</a></li>
                <li><a href="#section-2" className="legal-toc-link">2. Technical Measures &amp; Standards</a></li>
                <li><a href="#section-3" className="legal-toc-link">3. Ongoing Improvements</a></li>
                <li><a href="#section-4" className="legal-toc-link">4. Personalized Accommodations</a></li>
                <li><a href="#section-5" className="legal-toc-link">5. Feedback &amp; Contact</a></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Accessibility;
